import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, Dropzone, Field, Input, Notice, Segmented, Textarea } from '@raro/ui';
import { useState } from 'react';
import { api } from '../lib/api';
import type { Source } from '../lib/types';

type Mode = 'file' | 'url' | 'text';

/** "+ colocar na Machine" — arquivo, link ou texto. */
export function AddSourceDrawer({
  open,
  onClose,
  organizationId,
  projectId,
  sessionId,
  eyebrow,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  projectId?: string | null;
  /** Fonte de uma sessão do VER: gravação, fotos, notas. Herda projeto e ciclo da sessão. */
  sessionId?: string | null;
  eyebrow?: string;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('file');
  const [files, setFiles] = useState<File[]>([]);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const reset = () => {
    setFiles([]);
    setUrl('');
    setText('');
    setTitle('');
    setDescription('');
  };

  const submit = useMutation({
    mutationFn: async () => {
      const base = `/organizations/${organizationId}/sources`;
      const common = { projectId: projectId ?? undefined, sessionId: sessionId ?? undefined, title: title || undefined, description: description || undefined };
      if (mode === 'file') {
        const created: Source[] = [];
        for (const f of files) {
          const form = new FormData();
          form.append('file', f);
          if (projectId) form.append('projectId', projectId);
          if (sessionId) form.append('sessionId', sessionId);
          if (files.length === 1 && title) form.append('title', title);
          if (description) form.append('description', description);
          created.push(await api.post<Source>(base, form));
        }
        return created;
      }
      if (mode === 'url') return [await api.post<Source>(base, { ...common, url: url.trim() })];
      return [await api.post<Source>(base, { ...common, text: text.trim() })];
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sources'] });
      await qc.invalidateQueries({ queryKey: ['session'] });
      await qc.invalidateQueries({ queryKey: ['sessions'] });
      await qc.invalidateQueries({ queryKey: ['overview'] });
      reset();
      onClose();
    },
  });

  const canSubmit = mode === 'file' ? files.length > 0 : mode === 'url' ? /^https?:\/\/\S+/.test(url.trim()) : text.trim().length > 2;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={eyebrow ?? "+ colocar na Machine"}
      title="o que você viu, ouviu ou encontrou?"
      footer={
        <>
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
            a Machine extrai, fragmenta e indexa. nada vira fato sem revisão.
          </span>
          <Button variant="primary" disabled={!canSubmit || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? 'enviando…' : 'colocar na Machine'}
          </Button>
        </>
      }
    >
      <div className="rr-stack rr-stack--5">
        <Segmented<Mode>
          value={mode}
          onChange={setMode}
          options={[
            { value: 'file', label: 'arquivo' },
            { value: 'url', label: 'link' },
            { value: 'text', label: 'texto' },
          ]}
        />

        {mode === 'file' && <Dropzone files={files} onFiles={setFiles} />}
        {mode === 'url' && (
          <Field label="Link" htmlFor="src-url" hint="páginas, artigos, reviews, notícias. a Machine lê o conteúdo principal.">
            <Input id="src-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" autoFocus />
          </Field>
        )}
        {mode === 'text' && (
          <Field label="Texto" htmlFor="src-text" hint="nota de campo, frase ouvida, transcrição colada, observação.">
            <Textarea id="src-text" value={text} onChange={(e) => setText(e.target.value)} rows={8} autoFocus placeholder="o que você observou?" />
          </Field>
        )}

        {(mode !== 'file' || files.length === 1) && (
          <Field label="Título" htmlFor="src-title" hint="opcional. sem título, a Machine usa o nome do arquivo, o título da página ou a primeira linha.">
            <Input id="src-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
        <Field label="Contexto" htmlFor="src-desc" hint="opcional. quem, onde, quando. ajuda a Machine e a Raro a situar a fonte.">
          <Textarea id="src-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </Field>

        {submit.isError && <Notice tone="error">{(submit.error as Error).message}</Notice>}
      </div>
    </Drawer>
  );
}
